'use client';

import {
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import type { SavedQuery } from '@repo/api-gw-sdk';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { DeleteSavedQueryDialog } from './deleteSavedQueryDialog';
import { DuplicateSavedQueryDialog } from './duplicateSavedQueryDialog';
import { EditSavedQueryDialog } from './editSavedQueryDialog';

interface SavedQueryPanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const SavedQueryPanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: SavedQueryPanelProps) => {
  const dal = useDAL();
  const router = useRouter();
  const { isAuthorized } = useRoles();
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const { data: queriesData, isLoading: isQueriesLoading } =
    dal.lakehouse.useListSavedQueries(workspaceId);

  const savedQuery = queriesData?.savedQueries?.find(
    (q: SavedQuery) => q.id === id
  );

  if (isQueriesLoading || !savedQuery) {
    return (
      <PanelWrapper
        header={{
          title: 'Saved Query Details',
          onClose,
        }}
      >
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canCreateQuery = isAuthorized('create:saved_query');
  const canUpdateQuery = isAuthorized('update:saved_query');
  const canDeleteQuery = isAuthorized('delete:saved_query');

  const tags = savedQuery.tags ?? [];

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Saved Query Details',
          onClose,
          actions: (
            <OptionMenu
              options={[
                {
                  id: 'open',
                  text: 'Open in Query Editor',
                  icon: 'material-symbols-open-in-new-rounded',
                  menuItemProps: {
                    onClick: () => {
                      router.push(
                        `/lakehouse/query-editor?savedQueryId=${savedQuery.id}`
                      );
                      onClose();
                    },
                  },
                },
                {
                  id: 'edit',
                  text: 'Edit',
                  icon: 'material-symbols-edit-outline-rounded',
                  menuItemProps: {
                    disabled: !canUpdateQuery,
                    onClick: () => setEditDialogOpen(true),
                  },
                },
                {
                  id: 'duplicate',
                  text: 'Duplicate',
                  icon: 'material-symbols-content-copy-outline-rounded',
                  menuItemProps: {
                    disabled: !canCreateQuery,
                    onClick: () => setDuplicateDialogOpen(true),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDeleteQuery,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='24px' padding='24px 40px'>
          {/* Query Details */}
          <Stack gap='12px'>
            <DetailRow label='ID' value={savedQuery.id} monospace />
            <DetailRow label='Name' value={savedQuery.name} />
            <DetailRow
              label='Description'
              value={savedQuery.description ?? '-'}
            />
            <DetailRow
              label='Created'
              value={dayjs
                .utc(savedQuery.createdAt)
                .format('MMM D, YYYY HH:mm:ss')}
            />
            {savedQuery.updatedAt && (
              <DetailRow
                label='Updated'
                value={dayjs
                  .utc(savedQuery.updatedAt)
                  .format('MMM D, YYYY HH:mm:ss')}
              />
            )}
            <DetailRow
              label='Tags'
              value={
                tags.length > 0 ? (
                  <Stack direction='row' gap='4px' flexWrap='wrap'>
                    {tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size='small'
                        variant='outlined'
                      />
                    ))}
                  </Stack>
                ) : (
                  '-'
                )
              }
            />
          </Stack>

          <Divider />

          {/* SQL Query */}
          <Stack gap='12px'>
            <Typography variant='subtitle2' color='text.secondary'>
              SQL Query
            </Typography>
            <Typography
              variant='body2'
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                backgroundColor: 'var(--mui-palette-action-hover)',
                padding: '12px',
                borderRadius: '8px',
              }}
            >
              {savedQuery.query}
            </Typography>
          </Stack>
        </Stack>
      </PanelWrapper>

      {editDialogOpen && (
        <EditSavedQueryDialog
          savedQuery={savedQuery}
          workspaceId={workspaceId}
          open={true}
          onClose={() => setEditDialogOpen(false)}
          onUpdated={() => onRefresh()}
        />
      )}

      {duplicateDialogOpen && (
        <DuplicateSavedQueryDialog
          savedQuery={savedQuery}
          workspaceId={workspaceId}
          open={true}
          onClose={() => setDuplicateDialogOpen(false)}
          onDuplicated={onRefresh}
        />
      )}

      <DeleteSavedQueryDialog
        savedQuery={savedQuery}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
  monospace,
}: {
  label: string;
  value?: React.ReactNode;
  monospace?: boolean;
}) => {
  return (
    <Stack direction='row' alignItems='flex-start' gap='16px'>
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{ minWidth: '120px' }}
      >
        {label}
      </Typography>
      {typeof value === 'string' ? (
        <Typography
          variant='body2'
          sx={{
            wordBreak: 'break-word',
            flex: 1,
            ...(monospace && { fontFamily: 'monospace', fontSize: '0.85rem' }),
          }}
        >
          {value || '-'}
        </Typography>
      ) : (
        (value ?? (
          <Typography variant='body2' sx={{ flex: 1 }}>
            -
          </Typography>
        ))
      )}
    </Stack>
  );
};
